import { useCallback, useContext } from 'react';
import { InfluencerPlanContext } from '../InfluencerPlanContext';
import useNonNullContext from '../../../../../Hooks/useNonNullContext';
import { TiktokCreatorsDispatchContext, TiktokCreatorsStateContext } from './TiktokCreatorsContext';
import { setManualStatusOnTiktokInfluencerPost } from '../../InfluencerPlan.api';
import { isNotNullOrUndefined, isNumber } from '../../../../../utility/utility';
import {
    fetchTiktokUserStats,
    getTiktokInfluencerUser,
    getTiktokUserImages,
    getTiktokUsers,
    patchTiktokInfluencerUser,
    TiktokInfluencerUser,
    TiktokInfluencerUserApiBody,
    TiktokUser,
    TiktokUserStats,
    getAllTiktokInfluencerPosts,
    getTiktokInfluencerPost,
    getPostPaymentRequests,
    PostPaymentRequest,
    TiktokVideoStats,
    createBatchedParamsArrayFromIds,
    getTiktokVideos,
    getTiktokVideoStats,
    TiktokVideo,
} from '@round/api';
import { getTiktokInfluencerUsers } from '../../../../TikTok/TikTok.api';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { showNotification } from '../../../../../helpers';
import { TiktokInfluencerPostRow } from '../../features/TiktokTable/TiktokInfluencerPostsTable/TiktokInfluencerPostsTable';
import { TiktokInfluencerPostApiBody } from '../../types/Tiktok.types';
import { patchTiktokInfluencerPost } from '../../api/Tiktok.api';
import { InfluencerPostManualStatus } from '../../InfluencerPlan.types';
import { TiktokInfluencerPost } from '@round/api';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { useAllCreatorbasePostsByPostIds } from '@round/creatorbase-helpers/api-hooks';

export default function useCreatorPlanTiktokInfluencerPosts() {
    const userHasPaymentsAccess = useCheckUserGroupsAccess(['round_planner', 'round_finance'], 'any');

    const { influencerPlan } = useContext(InfluencerPlanContext);
    const state = useNonNullContext(TiktokCreatorsStateContext);
    const dispatch = useNonNullContext(TiktokCreatorsDispatchContext);

    const influencerPostIds = state.tiktokInfluencerPosts.map((p) => p.influencer_post_id).filter(isNotNullOrUndefined);

    const { data: creatorbasePosts = [] } = useAllCreatorbasePostsByPostIds(influencerPostIds, {
        enabled: Boolean(influencerPostIds.length > 0 && userHasPaymentsAccess),
    });

    const fetchInfluencerUsers = useCallback(
        async (ids: number[], requestInit?: RequestInit): Promise<TiktokInfluencerUser[]> => {
            if (!ids.length) {
                return [];
            }

            return getTiktokInfluencerUsers({ id: ids.toString(), page_size: ids.length }, requestInit).then(
                (r) => r.data.results
            );
        },
        []
    );

    const fetchVideoStats = useCallback(
        async (ids: number[], requestInit?: RequestInit): Promise<TiktokVideoStats[]> => {
            if (!ids.length) {
                return [];
            }

            const batchedParams = createBatchedParamsArrayFromIds(
                ids,
                (ids) => ({
                    video_ids: ids.toString(),
                    latest: true,
                }),
                50
            );

            return Promise.all(batchedParams.map((params) => getTiktokVideoStats(params, requestInit))).then(
                (responses) => {
                    return responses.reduce<TiktokVideoStats[]>((acc, response) => {
                        if (response.status === 200) {
                            return acc.concat(response.data.results);
                        }
                        return acc;
                    }, []);
                }
            );
        },
        []
    );

    const fetchUserStats = useCallback(async (ids: number[], requestInit?: RequestInit): Promise<TiktokUserStats[]> => {
        if (!ids.length) {
            return [];
        }

        const batchedParams = createBatchedParamsArrayFromIds(
            ids,
            (ids) => ({
                user_ids: ids,
                latest: true,
            }),
            50
        );

        return Promise.all(batchedParams.map((params) => fetchTiktokUserStats(params, requestInit))).then(
            (responses) => {
                return responses.reduce<TiktokUserStats[]>((acc, response) => {
                    if (response.status === 200) {
                        return acc.concat(response.data.results);
                    }
                    return acc;
                }, []);
            }
        );
    }, []);

    const fetchVideos = useCallback(async (ids: number[], requestInit?: RequestInit) => {
        if (!ids.length) {
            return [];
        }

        const batchedParams = createBatchedParamsArrayFromIds(
            ids,
            (ids) => ({
                id: ids.toString(),
            }),
            50
        );

        return Promise.all(batchedParams.map((params) => getTiktokVideos(params, requestInit))).then((responses) => {
            return responses.reduce<TiktokVideo[]>((acc, response) => {
                if (response.status === 200) {
                    return acc.concat(response.data.results);
                }
                return acc;
            }, []);
        });
    }, []);

    const fetchPaymentRequests = useCallback(
        async (ids: number[], requestInit?: RequestInit) => {
            if (!ids.length || !userHasPaymentsAccess) {
                return [];
            }
            return getPostPaymentRequests({ id: ids.toString(), page_size: ids.length }, requestInit).then((r) =>
                r.status === 200 ? r.data.results : []
            );
        },
        [userHasPaymentsAccess]
    );

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            if (!influencerPlan?.id) {
                return;
            }

            dispatch({ type: 'loadTiktokInfluencerPostData' });
            try {
                const influencerPosts = await getAllTiktokInfluencerPosts({ plan_id: influencerPlan.id }, requestInit);

                const influencerUserIds = Array.from(
                    new Set(influencerPosts.map((p) => p.influencer_id).filter(isNumber))
                );
                const videoIds = influencerPosts.map((p) => p.tiktok_post).filter(isNumber);
                const paymentRequestIds = influencerPosts.map((p) => p.payment_request_id).filter(isNumber);
                const [influencerUsers, videos, videoStats, paymentRequests] = await Promise.all([
                    fetchInfluencerUsers(influencerUserIds, requestInit),
                    fetchVideos(videoIds, requestInit),
                    fetchVideoStats(videoIds, requestInit),
                    fetchPaymentRequests(paymentRequestIds, requestInit),
                ]);

                let userStats: TiktokUserStats[] = [];
                let users: TiktokUser[] = [];
                const userIds = influencerUsers.map((influencer) => influencer.user).filter(isNumber);
                if (userIds.length) {
                    const [fetchedUserStats, usersResponse] = await Promise.all([
                        fetchUserStats(userIds, requestInit),
                        getTiktokUsers({ id: userIds.join(), page_size: userIds.length }, requestInit),
                    ]);

                    userStats = fetchedUserStats;
                    users = usersResponse.status === 200 ? usersResponse.data.results : [];
                }

                dispatch({
                    type: 'tiktokInfluencerPostDataInitialized',
                    payload: {
                        tiktokInfluencerPosts: influencerPosts,
                        tiktokInfluencerUsers: influencerUsers,
                        tiktokUserStats: userStats,
                        tiktokVideos: videos,
                        tiktokVideoStats: videoStats,
                        tiktokUsers: users,
                        tiktokPaymentRequests: paymentRequests,
                    },
                });
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                dispatch({
                    type: 'setTiktokInfluencerPostLoadingError',
                    payload: "Sorry, we're experiencing technical issues",
                });
            }
        },
        [
            dispatch,
            fetchInfluencerUsers,
            fetchUserStats,
            fetchVideoStats,
            fetchVideos,
            fetchPaymentRequests,
            influencerPlan?.id,
        ]
    );

    useAbortableEffect(
        (signal) => {
            const userIds = state.tiktokInfluencerUsers.map((influencer) => influencer.user).filter(isNumber);
            if (!userIds.length) {
                return;
            }

            getTiktokUserImages(userIds, { signal })
                .then((images) => {
                    dispatch({ type: 'tiktokUserImagesInitialized', payload: images });
                })
                .catch((e) => {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }

                    showNotification('Could not fetch user images', 'error');
                });
        },
        [dispatch, state.tiktokInfluencerUsers]
    );

    const updateInfluencerPost = useCallback(
        async (postId: number, data: Partial<TiktokInfluencerPostApiBody>) => {
            const response = await patchTiktokInfluencerPost(postId, data);

            if (response.status === 200) {
                dispatch({ type: 'influencerPostUpdated', payload: response.data });

                const influencer = state.tiktokInfluencerUsers.find(
                    (influencer) => influencer.id === response.data.influencer_id
                );

                if (!influencer && response.data.influencer_id) {
                    const influencerUser = await getTiktokInfluencerUser(response.data.influencer_id);
                    dispatch({ type: 'addTiktokInfluencerUser', payload: influencerUser });
                }

                return response;
            }

            return response;
        },
        [dispatch, state.tiktokInfluencerUsers]
    );

    const refreshInfluencerPost = useCallback(
        async (postId: number) => {
            const response = await getTiktokInfluencerPost(postId);
            if (response.status === 200) {
                dispatch({ type: 'influencerPostUpdated', payload: response.data });
            }

            return response;
        },
        [dispatch]
    );

    const refreshTiktokInfluencerPostsForGroup = useCallback(
        async (groupId: number, requestInit?: RequestInit) => {
            const postIds = state.tiktokInfluencerPosts.filter((p) => p.group_id === groupId).map((p) => p.id);
            if (!postIds.length) {
                return;
            }

            dispatch({ type: 'setRefreshingGroupId', payload: groupId });

            try {
                const groupPosts = await getAllTiktokInfluencerPosts({ id: postIds.join(',') }, requestInit);
                for (const post of groupPosts) {
                    dispatch({ type: 'influencerPostUpdated', payload: post });
                }
            } finally {
                dispatch({ type: 'setRefreshingGroupId', payload: null });
            }
        },
        [dispatch, state.tiktokInfluencerPosts]
    );

    const updateInfluencerUser = useCallback(
        async (influencerId: number, data: Partial<TiktokInfluencerUserApiBody>) => {
            const response = await patchTiktokInfluencerUser(influencerId, data);
            if (response.status === 200) {
                dispatch({ type: 'influencerUserUpdated', payload: response.data });
                return response;
            }

            return response;
        },
        [dispatch]
    );

    const updateInfluencerPostStatus = useCallback(
        async (data: InfluencerPostManualStatus) => {
            await setManualStatusOnTiktokInfluencerPost(data);
            const response = await getTiktokInfluencerPost(data.influencer_post_id);
            if (response.status === 200) {
                dispatch({ type: 'influencerPostUpdated', payload: response.data });
                return;
            }
            throw new Error('Could not update influencer post status');
        },
        [dispatch]
    );

    const addPaymentRequest = useCallback(
        (paymentRequest: PostPaymentRequest) => {
            dispatch({ type: 'addPaymentRequest', payload: paymentRequest });
        },
        [dispatch]
    );

    return {
        init,
        initialized: state.tiktokInfluencerPostDataInitialized,
        error: state.tiktokInfluencerPostDataLoadingError,
        loading: state.tiktokInfluencerPostDataLoading,
        refreshingGroupId: state.refreshingGroupId,
        refreshTiktokInfluencerPostsForGroup,
        rows: state.tiktokInfluencerPosts.map<TiktokInfluencerPostRow>((post) => {
            const influencer = state.tiktokInfluencerUsers.find((inf) => inf.id === post.influencer_id);
            const postTiktokUserId = influencer?.user ?? post.tiktok_user;
            return {
                ...post,
                influencer,
                user: state.tiktokUsers.find((user) => user.id === postTiktokUserId),
                userStats: state.tiktokUserStats.find((stats) => stats.user_id === postTiktokUserId),
                userImage: state.tiktokUserImages.find((image) => image.user_id === postTiktokUserId),
                tiktokVideo: state.tiktokVideos.find((video) => video.id === post.tiktok_post),
                tiktokVideoStats: state.tiktokVideoStats.find((stats) => stats.video_id === post.tiktok_post),
                paymentRequest:
                    state.tiktokPaymentRequests.find((request) => request.id === post.payment_request_id) || null,
                creatorbasePost: creatorbasePosts.find((p) => p.id === post.influencer_post_id) ?? null,
            };
        }),
        posts: state.tiktokInfluencerPosts,
        influencerUsers: state.tiktokInfluencerUsers,
        users: state.tiktokUsers,
        userStats: state.tiktokUserStats,
        userImages: state.tiktokUserImages,
        videos: state.tiktokVideos,
        videoStats: state.tiktokVideoStats,
        refreshInfluencerPost,
        updateInfluencerPost,
        updateInfluencerUser,
        updateInfluencerPostStatus,
        setInfluencerUser: (influencer: TiktokInfluencerUser) =>
            dispatch({ type: 'influencerUserUpdated', payload: influencer }),
        setInfluencerPost: (post: TiktokInfluencerPost) => dispatch({ type: 'influencerPostUpdated', payload: post }),
        addPaymentRequest,
    };
}
